import {
  normalizeSearchText,
  searchMatchScore,
  searchTokens,
} from './erp-search-match';

// pipe 4C: pure, dependency-free row filter + sort for the ERP table screens (the Vitest seam, a
// sibling of humanize.ts / stage-label.ts). It operates ONLY on already-redacted, display-ready
// values: the server has authorized + redacted every row before it reaches the client, so a masked
// field is simply absent from the accessor output and is therefore unmatchable / sorts last. Client
// filtering and sorting are a viewing convenience, NEVER a security boundary.

export type SortDir = 'asc' | 'desc';
export type ColumnType = 'text' | 'number' | 'boolean' | 'timestamp';

/** The current sort selection. `key === null` means "fetch order" (no client sort applied). */
export interface SortState {
  key: string | null;
  dir: SortDir;
}

/** A sortable column: a stable `key`, the value `accessor`, and the comparator `type` (defaults to
 *  text). `type` should be derived from the entity-schema FieldSpec.type where available (SSOT). */
export interface SortColumn<T> {
  key: string;
  type?: ColumnType;
  accessor: (row: T) => unknown;
}

const isNullish = (v: unknown): boolean =>
  v === null || v === undefined || v === '';

/** Shared normalized, order-independent token matching over display-ready values. Exact values
 * and prefixes rank first; nulls are ignored and an empty query keeps every row. */
export function filterRows<T>(
  rows: readonly T[],
  query: string,
  accessor: (row: T) => Array<string | null | undefined>
): T[] {
  const tokens = searchTokens(query);
  if (!tokens.length) return rows.slice();
  // Lists share normalization and ranking. Typo suggestions arrive separately, labelled by
  // the server; table filtering cannot guess which accessor strings represent identifiers.
  return rows
    .map(row => ({
      row,
      score: searchMatchScore(
        {
          option: row,
          parts: accessor(row)
            .filter((v): v is string => typeof v === 'string')
            .map(normalizeSearchText),
        },
        tokens
      ),
    }))
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map(r => r.row);
}

/** scan-hardening: an already-redacted row `data` projection → its searchable primitive strings.
 *  A scan deep-links a list with `?q=<code>`, and the code is often NOT the row's title (a drive
 *  is titleless — its serial lives in `data`; an asset's manufacturer serial/IMEI differ from the
 *  tag the title carries), so the filter haystack must include the visible data values or every
 *  such scan dead-ends on "no matches". Masked scalars arrive as `null` (excluded naturally) and
 *  their `<key>_redacted: true` markers are skipped (a "true" query must not match every row) —
 *  still display-ready, already-redacted values only, never a security boundary. */
export function visibleDataStrings(
  data: Record<string, unknown> | null | undefined
): string[] {
  if (!data) return [];
  const out: string[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (key.endsWith('_redacted')) continue;
    if (value === null || value === undefined || value === '') continue;
    const kind = typeof value;
    if (kind === 'string' || kind === 'number' || kind === 'boolean') {
      out.push(String(value));
    }
  }
  return out;
}

type Cmp = (a: unknown, b: unknown, collator: Intl.Collator) => number;

// Collator construction is one of the pricier Intl instantiations and sortRows runs once per
// keystroke/re-sort in every ERP grid — cache one instance per locale (options are fixed).
const collators = new Map<string, Intl.Collator>();
function collatorFor(locale?: string): Intl.Collator {
  const key = locale ?? '';
  let collator = collators.get(key);
  if (!collator) {
    collator = new Intl.Collator(locale, {
      numeric: true,
      sensitivity: 'base',
    });
    collators.set(key, collator);
  }
  return collator;
}

function comparatorFor(type?: ColumnType): Cmp {
  switch (type) {
    case 'number':
      return (a, b, c) => {
        const x = Number(a);
        const y = Number(b);
        return Number.isNaN(x) || Number.isNaN(y)
          ? c.compare(String(a), String(b))
          : x - y;
      };
    case 'boolean':
      // false < true
      return (a, b) => (a === b ? 0 : a ? 1 : -1);
    case 'timestamp':
      return (a, b, c) => {
        const x = Date.parse(String(a));
        const y = Date.parse(String(b));
        return Number.isNaN(x) || Number.isNaN(y)
          ? c.compare(String(a), String(b))
          : x - y;
      };
    case 'text':
    default:
      return (a, b, c) => c.compare(String(a), String(b));
  }
}

/** Stable, type-aware sort. Nullish values (null/undefined/'') always sort LAST, in BOTH directions
 *  (so a re-sort never floats blanks to the top). Text uses a locale-aware `Intl.Collator` with
 *  natural numeric ordering ('2' < '10'). Equal values are broken by `tieBreak` (a stable key such as
 *  id) so the order is deterministic across re-sorts. Returns a new array; `key === null` returns the
 *  input order unchanged. */
export function sortRows<T>(
  rows: readonly T[],
  state: SortState,
  columns: ReadonlyArray<SortColumn<T>>,
  tieBreak?: (row: T) => string,
  locale?: string
): T[] {
  const out = rows.slice();
  if (state.key === null) return out;
  const col = columns.find(c => c.key === state.key);
  if (!col) return out;
  const collator = collatorFor(locale);
  const cmp = comparatorFor(col.type);
  const tie = (a: T, b: T): number =>
    tieBreak ? collator.compare(tieBreak(a), tieBreak(b)) : 0;
  out.sort((a, b) => {
    const av = col.accessor(a);
    const bv = col.accessor(b);
    const an = isNullish(av);
    const bn = isNullish(bv);
    if (an && bn) return tie(a, b);
    if (an) return 1; // a is nullish → sorts last regardless of direction
    if (bn) return -1; // b is nullish → sorts last regardless of direction
    let r = cmp(av, bv, collator);
    if (state.dir === 'desc') r = -r;
    return r !== 0 ? r : tie(a, b);
  });
  return out;
}

/** Header-click state machine: first click on a column → ascending; second → descending; third →
 *  cleared (back to fetch order). Clicking a different column starts it ascending. */
export function toggleSort(state: SortState, key: string): SortState {
  if (state.key !== key) return { key, dir: 'asc' };
  if (state.dir === 'asc') return { key, dir: 'desc' };
  return { key: null, dir: 'asc' };
}
